import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { getFieldReports, createFieldReport, updateReportStatus } from '@/lib/api'
import { formatDateTime } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { useSessionContext } from '@/components/SessionProvider'
import { Plus, FileText, Check, X, AlertCircle, MapPin, User, Clock, Send, CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'

function getPriorityBadgeClass(priority: string) {
  switch (priority) {
    case 'critical': return 'sanctuary-badge sanctuary-badge-critical'
    case 'high': return 'sanctuary-badge sanctuary-badge-high'
    case 'medium': return 'sanctuary-badge sanctuary-badge-medium'
    default: return 'sanctuary-badge sanctuary-badge-low'
  }
}

function getStatusBadgeClass(status: string) {
  switch (status) {
    case 'resolved': return 'sanctuary-badge sanctuary-badge-completed'
    case 'reviewed': return 'sanctuary-badge sanctuary-badge-active'
    case 'false_alarm': return 'sanctuary-badge sanctuary-badge-low'
    default: return 'sanctuary-badge sanctuary-badge-pending'
  }
}

export default function FieldReports() {
  const [showForm, setShowForm] = useState(false)
  const [filter, setFilter] = useState<string>('')
  const [urgency, setUrgency] = useState('low')
  const [submitted, setSubmitted] = useState(false)
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { session } = useSessionContext()

  const { data: reports, isLoading } = useQuery({
    queryKey: ['fieldReports', { status: filter }],
    queryFn: () => getFieldReports({ status: filter || undefined }),
  })

  const createMutation = useMutation({
    mutationFn: createFieldReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fieldReports'] })
      queryClient.invalidateQueries({ queryKey: ['stats'] })
      setSubmitted(true)
      setTimeout(() => {
        setSubmitted(false)
        setShowForm(false)
        setUrgency('low')
      }, 2000)
      toast({ title: 'Report submitted successfully' })
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to submit report', description: error.message, variant: 'destructive' })
    },
  })

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      updateReportStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fieldReports'] })
      queryClient.invalidateQueries({ queryKey: ['stats'] })
      toast({ title: 'Status updated' })
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to update status', description: error.message, variant: 'destructive' })
    },
  })

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data = {
      airport_code: formData.get('airport_code'),
      terminal: formData.get('terminal'),
      gate: formData.get('gate'),
      flight_number: formData.get('flight_number'),
      priority: urgency,
      subject_description: formData.get('subject_description'),
      companion_description: formData.get('companion_description'),
      notes: formData.get('notes'),
      indicators: [],
    }
    createMutation.mutate(data)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Field Reports</h1>
          <p className="text-sm text-muted-foreground">Suspicious activity observations</p>
        </div>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          New Report
        </Button>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['', 'submitted', 'reviewed', 'resolved'].map((status) => (
          <Button
            key={status}
            variant={filter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(status)}
          >
            {status || 'All'}
          </Button>
        ))}
      </div>

      {/* New Report Form */}
      {showForm && (
        submitted ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="bg-primary/10 p-6 rounded-full mb-6">
              <CheckCircle2 size={48} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Report Submitted</h2>
            <p className="text-muted-foreground text-center max-w-sm">
              Your observation has been sent to the Task Force Dispatch. A response team will be notified immediately.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Reporting As Card */}
            <Card className="bg-card border-border shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Reporting As</p>
                    <p className="font-semibold">{session?.user?.name || 'Unknown'}</p>
                    <p className="text-sm text-muted-foreground">{session?.user?.email}</p>
                  </div>
                  <div className="bg-primary/10 p-3 rounded-full">
                    <User size={24} className="text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Alert Banner */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-4 flex gap-3">
                <AlertCircle size={20} className="text-primary shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-sm text-foreground">Observation Alert</h3>
                  <p className="text-xs text-muted-foreground">
                    Information sent directly to Task Force Dispatch for immediate review.
                  </p>
                </div>
              </CardContent>
            </Card>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Location Information */}
              <Card className="bg-card border-border shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <MapPin size={16} className="text-primary" />
                    Location Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Airport Code *</label>
                      <input
                        name="airport_code"
                        required
                        className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                        placeholder="e.g. LAX"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Flight Number</label>
                      <input
                        name="flight_number"
                        className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                        placeholder="e.g. UA2410"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Terminal</label>
                      <input
                        name="terminal"
                        className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                        placeholder="Terminal A"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Gate</label>
                      <input
                        name="gate"
                        className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                        placeholder="B12"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Person of Concern */}
              <Card className="bg-card border-border shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User size={16} className="text-primary" />
                    Person of Concern
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-muted-foreground uppercase">
                      Identifying Characteristics *
                    </label>
                    <textarea
                      name="subject_description"
                      required
                      rows={3}
                      className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                      placeholder="Race, clothing, height, hair color, approximate age, distinguishing features..."
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-muted-foreground uppercase">Accompanied By</label>
                    <input
                      name="companion_description"
                      className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                      placeholder="Description of companion(s)"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Urgency & Notes */}
              <Card className="bg-card border-border shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Clock size={16} className="text-primary" />
                    Urgency Level
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'low', label: 'Low', color: 'bg-muted hover:bg-muted/80' },
                      { value: 'medium', label: 'Medium', color: 'bg-amber-100/60 hover:bg-amber-100 text-amber-700' },
                      { value: 'high', label: 'High', color: 'bg-destructive/20 hover:bg-destructive/30 text-destructive' },
                    ].map((level) => (
                      <button
                        key={level.value}
                        type="button"
                        onClick={() => setUrgency(level.value)}
                        className={cn(
                          'p-3 rounded-lg text-xs font-semibold transition-all',
                          level.color,
                          urgency === level.value ? 'ring-2 ring-primary' : ''
                        )}
                      >
                        {level.label}
                      </button>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-muted-foreground uppercase">Additional Notes</label>
                    <textarea
                      name="notes"
                      rows={2}
                      className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                      placeholder="Behavior observed, concerns, any other relevant details..."
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex-1 py-5 text-base font-bold gap-2"
                >
                  <Send size={18} />
                  {createMutation.isPending ? 'Submitting...' : 'Submit Observation'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setUrgency('low') }}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )
      )}

      {/* Reports List */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : reports && reports.length > 0 ? (
        <div className="space-y-3">
          {reports.map((report: any) => (
            <Card key={report.id} className="transition-shadow hover:shadow-md">
              <CardContent className="p-4">
                <div className="flex justify-between items-start gap-3">
                  <div className="flex gap-3 min-w-0 flex-1">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm">
                        {report.airport_code} - {report.terminal || 'Unknown Terminal'}
                        {report.gate && ` / Gate ${report.gate}`}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Reported by {report.reporter_name} · {formatDateTime(report.created_at)}
                      </p>
                      {report.subject_description && (
                        <p className="mt-2 text-sm text-foreground/80 line-clamp-2">{report.subject_description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className={getPriorityBadgeClass(report.priority)}>
                      {report.priority}
                    </span>
                    <span className={getStatusBadgeClass(report.status)}>
                      {report.status}
                    </span>
                  </div>
                </div>
                {report.status === 'submitted' && (
                  <div className="mt-3 flex gap-2 ml-13">
                    <Button
                      size="sm"
                      onClick={() =>
                        statusMutation.mutate({ id: report.id, status: 'reviewed' })
                      }
                      className="gap-1"
                    >
                      <Check className="h-3.5 w-3.5" />
                      Mark Reviewed
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        statusMutation.mutate({ id: report.id, status: 'false_alarm' })
                      }
                      className="gap-1"
                    >
                      <X className="h-3.5 w-3.5" />
                      False Alarm
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <FileText className="h-10 w-10 mb-3 opacity-40" />
            <p className="font-medium">No field reports found</p>
            <p className="text-sm mt-1">Submit your first report to get started</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
